import { type ChangeEvent, useCallback, useMemo, useState } from 'react';
import { InputTextarea, type InputTextareaProps } from 'primereact/inputtextarea';
import { ReactComponent as ErrorIcon } from '../../../assets/shared/ic-shared-error.svg';
import { ReactComponent as ClearIcon } from '../../../assets/shared/ic-shared-clear.svg';
import { Text, useFontClass } from '../../tokens';

interface LiliTextAreaProps extends InputTextareaProps {
  isDisabled?: boolean;
  maxLength?: number;
  minLength?: number;
  placeholder?: string;
  value?: string;
  onValueChange?: (value: string) => void;
  errorMsg?: string;
  externalErrorMessage?: string;
  minHeight?: string;
  id?: string;
  onBlur?: () => void;
  onFocus?: () => void;
  onClear?: () => void;
  placeholderIcon?: React.ReactNode;
  leftComponent?: React.ReactNode;
}

export const LiliTextArea = ({
  isDisabled,
  maxLength,
  placeholder,
  value = '',
  onValueChange,
  errorMsg,
  minHeight,
  id,
  onBlur,
  onFocus,
  onClear,
  externalErrorMessage,
  minLength,
  placeholderIcon,
  leftComponent,
  ...rest
}: LiliTextAreaProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const { fontClass, placeholderFontClass } = useFontClass();

  const shouldShowPlaceholderIcon = placeholderIcon && !value;

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    if (onValueChange) onValueChange(e.target.value);
  };

  const handleClear = () => {
    if (onClear) onClear();
    if (onValueChange) onValueChange('');
  };

  const isInputInvalid = useMemo(() => {
    if (!maxLength) return false;
    return value.length > maxLength;
  }, [value, maxLength]);

  const getErrorMessage = useCallback(() => {
    if (maxLength && value.length > maxLength) return `Maximum ${maxLength} characters.`;
    if (externalErrorMessage) return externalErrorMessage;
    if (errorMsg) return errorMsg;
    return '';
  }, [externalErrorMessage, errorMsg, maxLength]);

  return (
    <div className='flex flex-col relative gap-[8px] rounded-[8px] w-full'>
      {value && (
        <button onClick={handleClear} type='button' className='absolute right-[8px] top-[8px]'>
          <ClearIcon className='w-[18px]' />
        </button>
      )}
      {shouldShowPlaceholderIcon && (
        <div className='absolute left-[12px] top-[8px]'>{placeholderIcon}</div>
      )}
      <InputTextarea
        maxLength={maxLength}
        minLength={minLength}
        id={id || ''}
        value={value}
        onBlur={() => {
          setIsFocused(false);
          if (onBlur) onBlur();
        }}
        onFocus={() => {
          setIsFocused(true);
          if (onFocus) onFocus();
        }}
        placeholder={placeholder}
        onChange={handleChange}
        className={`shadow-none bg-surface-primary-bw-01 hover:bg-surface-primary-bw-00 resize-none placeholder:text-text-main-secondary pl-[12px] pr-[40px] py-[8px] rounded-[8px] border ${
          isInputInvalid
            ? 'border-outline-negative-primary'
            : isFocused
              ? 'border-outline-brand-primary'
              : 'border-outline-primary-bw-04'
        } ${fontClass} ${placeholderFontClass} text-[14px] text-text-main-primary ${!value ? 'max-h-[37px]' : ''} ${
          minHeight || ''
        } ${shouldShowPlaceholderIcon ? 'pl-[40px]' : ''}`}
        disabled={isDisabled}
        autoResize
        data-hj-allow
        {...rest}
      />

      <div className='flex items-center justify-between'>
        {getErrorMessage() ? (
          <div
            className={`flex items-center break-words gap-[2px] ${getErrorMessage() ? 'opacity-100' : 'opacity-0'}`}>
            <ErrorIcon className='min-w-[14px]' />
            <Text.H10 className='text-text-negative-red'>{getErrorMessage()}</Text.H10>
          </div>
        ) : (
          (leftComponent ?? <div />)
        )}
        <div className='flex items-center'>
          <Text.H10
            className={
              maxLength && value.length > maxLength
                ? 'text-text-negative-red'
                : 'text-text-main-secondary'
            }>
            {value.length.toString()}
          </Text.H10>
          {maxLength && (
            <Text.H10 className='text-text-main-secondary'>{`/${maxLength.toString()}`}</Text.H10>
          )}
        </div>
      </div>
    </div>
  );
};
